/// Datas de negócio — compra, vencimento, reembolso — são datas civis, não instantes. Elas não têm
/// hora nem fuso: 15/08/2026 é o mesmo dia em qualquer lugar. Por isso circulam como `AAAA-MM-DD` e
/// nunca passam por `new Date()` com fuso, que empurraria o dia para trás ou para frente.

export type CivilDate = string;

const ISO = /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})$/u;

export function isCivilDate(value: string): boolean {
  const match = ISO.exec(value);
  if (!match?.groups) return false;
  const year = Number(match.groups.year);
  const month = Number(match.groups.month);
  const day = Number(match.groups.day);
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/// `AAAA-MM-DD` para `DD/MM/AAAA`.
export function formatCivilDate(value: CivilDate | null): string {
  if (value === null || !isCivilDate(value)) return '';
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
}

/// Aplica a máscara enquanto se digita, aceitando só dígitos.
export function maskCivilDate(input: string): string {
  const digits = input.replace(/\D/gu, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/// `DD/MM/AAAA` para `AAAA-MM-DD`, ou nulo enquanto a data não estiver completa e for válida.
export function parseCivilDate(input: string): CivilDate | null {
  const digits = input.replace(/\D/gu, '');
  if (digits.length !== 8) return null;
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4);
  const candidate = `${year}-${month}-${day}`;
  return isCivilDate(candidate) ? candidate : null;
}

export function todayCivilDate(timeZone: string): CivilDate {
  // `en-CA` produz exatamente AAAA-MM-DD, e o fuso decide qual é "hoje" para o negócio.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function civilDateParts(value: CivilDate): { year: number; month: number; day: number } {
  const [year, month, day] = value.split('-').map(Number);
  return { year: year!, month: month!, day: day! };
}

export function buildCivilDate(year: number, month: number, day: number): CivilDate {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/// Soma meses preservando o fim de mês: 31/01 + 1 mês é 28/02, não 03/03. É a regra que o carnê de
/// parcelas segue.
export function addMonths(value: CivilDate, months: number): CivilDate {
  const { year, month, day } = civilDateParts(value);
  const total = year * 12 + (month - 1) + months;
  const nextYear = Math.floor(total / 12);
  const nextMonth = (total % 12) + 1;
  return buildCivilDate(nextYear, nextMonth, Math.min(day, daysInMonth(nextYear, nextMonth)));
}

/// Um recorte de dias. As duas pontas podem faltar: ausente é "sem recorte", e só o começo é o
/// intervalo ainda aberto de quem escolheu o primeiro dia.
export interface DateRange {
  from: CivilDate | null;
  to: CivilDate | null;
}

/// Como o intervalo é escrito em toda parte: no botão que o abre e na etiqueta que o desfaz. Estar
/// aqui, e não em cada tela, é o que impede a etiqueta de dizer uma coisa e o botão outra.
export function formatCivilRange(range: DateRange, emptyLabel: string): string {
  const from = range.from ? formatCivilDate(range.from) : '';
  const to = range.to ? formatCivilDate(range.to) : '';
  if (!from && !to) return emptyLabel;
  if (from && !to) return `A partir de ${from}`;
  if (!from && to) return `Até ${to}`;
  return from === to ? from : `${from} – ${to}`;
}

/// Os meses por extenso, do jeito que a interface os escreve.
export const MONTHS_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/// O mesmo nome que o seletor de mês mostra, para quem só precisa dizer de que mês está falando.
export function formatMonth(value: string): string {
  const [year, month] = value.split('-').map(Number) as [number, number];
  return `${MONTHS_FULL[month - 1] ?? ''} de ${String(year)}`;
}
